import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { SubscriptionPlan } from '@modules-deprecated/app/organization/enums/subscriptionPlan'
import { resolveSubscriptionPlan } from '@views/settings/routes/OrganizationSubscriptionSettings/utils/resolveSubscriptionPlan'

import { useOrganizationMarket, useOrganizationSubscription } from '../../../../hooks'

interface UseNextSubscriptionPlanResult {
  isLoading: boolean
  nextPlanName: string
}

/**
 * Trial orgs have unrestricted feature access, and locked orgs are blocked
 * on a dedicated upgrade page before reaching the app — neither is expected
 * to actually hit the gated features that trigger this modal. The fallback
 * to the `Plus` plan's translated name below is just a defensive safety net
 * for those states (and for orgs without a resolvable upgrade target) so
 * `nextPlanName` is never left empty if that assumption ever turns out wrong.
 */
export const useNextSubscriptionPlan = (): UseNextSubscriptionPlanResult => {
  const { t } = useTranslation()
  const { isLoading: isLoadingOrganization } = useUserOrganization()
  const { market } = useOrganizationMarket()
  const { subscriptionPlan, isTrial, isLocked, isLoading: isLoadingSubscription } = useOrganizationSubscription()

  // The subscription query is disabled (idle, not "loading") until the organization itself has
  // loaded, so isLoadingSubscription alone briefly under-reports loading — fold in the
  // organization's own isLoading too.
  const isLoading = isLoadingOrganization || isLoadingSubscription

  const nextPlanName = useMemo(() => {
    const fallbackPlanName = t(resolveSubscriptionPlan(SubscriptionPlan.Plus, market || undefined).content.name)
    const isLockedNonTrial = !!isLocked && !isTrial

    if (isTrial || isLockedNonTrial || !subscriptionPlan) {
      return fallbackPlanName
    }

    const upgradableTo = resolveSubscriptionPlan(subscriptionPlan, market || undefined).behavior?.upgradableTo ?? []
    const nextPlan = upgradableTo[0]

    return nextPlan ? t(resolveSubscriptionPlan(nextPlan, market || undefined).content.name) : fallbackPlanName
  }, [subscriptionPlan, isTrial, isLocked, market, t])

  return { isLoading, nextPlanName }
}
